'use client';
import React from 'react';
import Link from 'next/link';
import { Cpu, Zap, Radio, Battery, ChevronRight } from 'lucide-react';

interface CategoriesProps {
  language: 'en' | 'ar';
  onQuoteOpen: (partNumber?: string) => void;
}

const categories = [
  {
    icon: <Cpu size={36} className="text-iraqi-red" />,
    nameEn: 'PLCs & Controllers',
    nameAr: 'وحدات التحكم المنطقية',
    descEn: 'Siemens, Rockwell, Mitsubishi',
    descAr: 'سيمنز، روكويل، ميتسوبيشي',
    count: '2,400+ SKUs',
    image: null,
    imageAlt: 'PLC and industrial controller units',
    slug: 'plcs-controllers',
  },
  {
    icon: <Zap size={36} className="text-iraqi-red" />,
    nameEn: 'Motor Drives & Inverters',
    nameAr: 'محركات ومحولات تردد',
    descEn: 'ABB, Siemens, Schneider VFDs',
    descAr: 'محركات ABB وسيمنز وشنايدر',
    count: '1,800+ SKUs',
    image: null,
    imageAlt: 'Industrial motor drive and inverter unit',
    slug: 'motor-drives-inverters',
  },
  {
    icon: <Radio size={36} className="text-iraqi-red" />,
    nameEn: 'Sensor Technology',
    nameAr: 'تقنية المستشعرات',
    descEn: 'Proximity, photoelectric, pressure',
    descAr: 'مستشعرات قرب وضغط وضوئية',
    count: '3,200+ SKUs',
    image: null,
    imageAlt: 'Industrial proximity and photoelectric sensors',
    slug: 'sensor-technology',
  },
  {
    icon: <Battery size={36} className="text-iraqi-red" />,
    nameEn: 'UPS & Power Systems',
    nameAr: 'أنظمة الطاقة والاحتياطي',
    descEn: 'Industrial UPS, power supplies, PDUs',
    descAr: 'طاقة احتياطية ومصادر طاقة صناعية',
    count: '950+ SKUs',
    image: null,
    imageAlt: 'Industrial UPS and power supply systems',
    slug: 'ups-power-systems',
  },
];

export default function Categories({ language }: CategoriesProps) {
  return (
    <section id="categories" className="py-16 bg-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-onyx font-space-grotesk mb-2">
            {language === 'ar' ? 'تصفح حسب الفئة' : 'Source by Category'}
          </h2>
          <p className="text-slate-grey font-roboto">
            {language === 'ar' ? 'خطوط مخزنة جاهزة للشحن خلال نفس الأسبوع.' : 'Stocked lines ready for same-week dispatch.'}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat, idx) => (
            <div
              key={idx}
              className="group bg-light-grey border border-slate-200 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-iraqi-red hover:shadow-card-hover"
            >
              {/* Icon or Image */}
              <div className="mb-4">
                {cat.image ? (
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-2">
                    <img
                      src={cat.image}
                      alt={cat.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center shadow-card border border-slate-100 group-hover:border-iraqi-red/30 transition-colors duration-200">
                    {cat.icon}
                  </div>
                )}
              </div>

              {/* Content */}
              <h3 className="font-semibold text-onyx font-space-grotesk mb-1 text-sm leading-tight">
                {language === 'ar' ? cat.nameAr : cat.nameEn}
              </h3>
              <p className="text-xs text-slate-grey font-roboto mb-3">
                {language === 'ar' ? cat.descAr : cat.descEn}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 font-roboto">{cat.count}</span>
                <Link
                  href={`/categories/${cat.slug}`}
                  className="flex items-center gap-1 text-iraqi-red text-xs font-semibold hover:gap-2 transition-all duration-200 font-space-grotesk"
                  aria-label={`View ${language === 'ar' ? cat.nameAr : cat.nameEn} products`}
                >
                  {language === 'ar' ? 'عرض المنتجات' : 'View Products'}
                  <ChevronRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
